import { useTranslation } from 'react-i18next';
import { Handshake } from 'lucide-react';
import { formatWithThousandSeparator } from '@/utils';

/**
 * The rung the merchant is on, and the ones above it.
 *
 * 🔴 NO PRICES. Each rung is marked with a handshake and nothing else, because
 * the number behind it is a negotiation with a human — the backend does not
 * send the other tiers' prices and this must never ask for them.
 *
 * Renders nothing when the service has no ladder (a custom plan, or a service
 * with no volume conditions). An empty rail promises a mechanism that does not
 * exist for that merchant.
 */
interface VolumeLadderProps {
  ladder: number[];
  orders: number;
}

const VolumeLadder = ({ ladder, orders }: VolumeLadderProps) => {
  const { t } = useTranslation();

  if (ladder.length === 0) {
    return null;
  }

  // One segment per band: the floor plus one per rung above it. `gte` is the
  // engine's operator, so a merchant exactly ON a threshold has cleared it.
  const cleared = ladder.filter((threshold) => orders >= threshold).length;
  const segments = ladder.length + 1;

  return (
    <div className='rounded-xl border border-sd-border bg-sd-muted/30 p-4'>
      <p className='m-0 text-xs font-medium text-sd-muted-foreground'>
        {String(t('pricing_plan_monthly_volume'))}
      </p>
      <p className='m-0 mt-0.5 text-sm font-semibold text-sd-foreground'>
        {/* `formatted`, not i18next's `count`: `count` is the plural selector and
            must stay numeric, but the number the merchant reads has to carry its
            separators — the counted card three lines up already prints 3,100. */}
        {String(t('pricing_plan_orders_this_month', { formatted: formatWithThousandSeparator(orders) }))}
      </p>

      {/* Handshake per rung, sitting over the boundary it marks. */}
      <div
        className='mt-4 grid gap-1'
        style={{ gridTemplateColumns: `repeat(${segments}, minmax(0, 1fr))` }}
        aria-hidden
      >
        {ladder.map((threshold, index) => (
          <Handshake
            key={threshold}
            className={`h-3.5 w-3.5 justify-self-end ${
              index < cleared ? 'text-sd-foreground' : 'text-sd-muted-foreground/60'
            }`}
            style={{ gridColumnStart: index + 1 }}
            strokeWidth={2}
          />
        ))}
      </div>

      <div
        className='mt-1.5 grid gap-1'
        style={{ gridTemplateColumns: `repeat(${segments}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: segments }, (_, index) => (
          <div
            key={index}
            className={`h-1.5 rounded-full ${index <= cleared ? 'bg-sd-primary' : 'bg-sd-border'}`}
          />
        ))}
      </div>

      <div
        className='mt-1.5 grid gap-1'
        style={{ gridTemplateColumns: `repeat(${segments}, minmax(0, 1fr))` }}
      >
        {ladder.map((threshold, index) => (
          <span
            key={threshold}
            className='justify-self-end text-[11px] tabular-nums text-sd-muted-foreground'
            style={{ gridColumnStart: index + 1 }}
          >
            <bdi dir='ltr'>{formatWithThousandSeparator(threshold)}</bdi>
          </span>
        ))}
      </div>

      <p className='m-0 mt-3 flex items-center gap-1.5 text-[11px] text-sd-muted-foreground'>
        <Handshake className='h-3.5 w-3.5 shrink-0' strokeWidth={2} aria-hidden />
        {String(t('pricing_plan_ladder_note'))}
      </p>
    </div>
  );
};

export default VolumeLadder;
